import React from "react";
import { useSelector } from "react-redux";

const getNiceMax = (rawMax) => {
  if (rawMax <= 0) return 1000;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const step = magnitude >= 100000 ? magnitude / 2 : magnitude / 4;
  return Math.ceil(rawMax / step) * step;
};

const formatYLabel = (value) => {
  if (value >= 1_000_000) return `$ ${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  if (value >= 1_000)     return `$ ${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}K`;
  return `$ ${Math.round(value)}`;
};

const BarChartSection = ({ data }) => {
  const { vesselCards } = useSelector((state) => state.rawData);
  const [eurToUsd, setEurToUsd] = React.useState(1.08); // fallback
  const cards_src = vesselCards || data || {};

  const eu_ets  = cards_src?.eu_ets  || {};
  const feum    = cards_src?.feum    || {};
  const uk_ets  = cards_src?.uk_ets  || {};
  const summary = cards_src?.summary || {};

  React.useEffect(() => {
  const fetchFX = async () => {
    try {
      const res = await fetch("https://open.er-api.com/v6/latest/USD");
      const data = await res.json();

      if (data.result === "success") {
        setEurToUsd(1 / data.rates.EUR);
      }
    } catch (err) {
      console.error("FX error:", err);
    }
  };

  fetchFX();
}, []);

  // Keep numeric value separate from display label
  const chartData = [
    {
      label:   "EU-ETS",
      value: (Number(eu_ets.cost) || 0) * eurToUsd,
      color:   "#6BADF5",
    },
    {
      label:   "FEUM",
      value: (Number(feum.cost) || 0) * eurToUsd,
      color:   "#b38ef3",
    },
    {
      label:   "UK ETS",
      value: (Number(uk_ets.cost) || 0) * eurToUsd,   
      color:   "#F5C36B",
    },
    {
      label:   "IMO GFI",
      value:   0,   
      color:   "#C7BFFF",
    },
  ];

  const rawMax = Math.max(...chartData.map((i) => i.value), 0);
  const yMax   = getNiceMax(rawMax);
  const TICKS  = 4;
  const step   = yMax / TICKS;

  const yLabels = Array.from({ length: TICKS + 1 }, (_, i) =>
    formatYLabel(yMax - i * step)
  );

  return (
    <div className="bar-chart-box">
      <h3 className="chart-box-title">Compliance Costs Breakdown ($)</h3>
      <div
        className="bar-chart-contner"
        style={{ display: "flex", alignItems: "stretch", height: "140px" }}
      >
        {/* Y-axis */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            paddingRight: "8px",
            fontSize: "10px",
            color: "#9ca3af",
            textAlign: "right",
            minWidth: "56px",
            flexShrink: 0,
          }}
        >
          {yLabels.map((label, i) => (
            <span key={i}>{label}</span>
          ))}
        </div>

        {/* Bars + Labels */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-around",
              gap: "6px",
              borderLeft: "1.5px solid #e5e7eb",
              borderBottom: "1.5px solid #e5e7eb",
              padding: "6px 8px 0 8px",
            }}
          >
            {chartData.map((item, i) => {
              const pct       = yMax > 0 ? (item.value / yMax) * 100 : 0;
              const barHeight = item.value > 0 ? Math.max(pct, 3) : 0;

              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-end",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  {/* Tooltip */}
                  {item.value > 0 && (
                    <div
                      className="bar-tooltip"
                      style={{
                        position: "absolute",
                        bottom: `${barHeight}%`,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#1e293b",
                        color: "#fff",
                        fontSize: "11px",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        whiteSpace: "nowrap",
                        opacity: 0,
                        pointerEvents: "none",
                        transition: "opacity 0.2s ease",
                      }}
                    >
                     $ {item.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  )}

                  {/* Bar */}
                  <div
                    className="bar-hover"
                    style={{
                      width: "100%",
                      borderRadius: "3px 3px 0 0",
                      transition: "height 0.3s ease",
                      cursor: item.value > 0 ? "pointer" : "default",
                      ...(item.value > 0
                        ? { height: `${barHeight}%`, backgroundColor: item.color }
                        : { height: "2px", backgroundColor: "#e5e7eb", borderRadius: 0 }
                      ),
                    }}
                    onMouseEnter={(e) => {
                      if (item.value > 0) {
                        const tooltip = e.currentTarget.previousSibling;
                        if (tooltip) tooltip.style.opacity = 1;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (item.value > 0) {
                        const tooltip = e.currentTarget.previousSibling;
                        if (tooltip) tooltip.style.opacity = 0;
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div style={{ display: "flex", justifyContent: "space-around", paddingTop: "5px" }}>
            {chartData.map((item, i) => (
              <span
                key={i}
                style={{
                  flex: 1,
                  fontSize: "10px",
                  color: "#6b7280",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarChartSection;
